import { useLingui } from '@lingui/react/macro'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { PageGraphSource } from 'features/knowledgeDocumentViewer/graph/canvasTypes'
import { usePageGraph } from 'features/knowledgeDocumentViewer/graph/usePageGraph'
import { useSystemDocumentPageByNum } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentPageByNum'
import { useMemo } from 'react'

import { useIsolationController } from './IsolationControllerContext'
import { useIsGraphLocked } from './useIsGraphLocked'
import { useIsGraphModeEnabled } from './useIsGraphModeEnabled'

export type GraphLoadState = {
  pageGraphs: PageGraphSource[]
  message: string | undefined
  failed: boolean
  retry: () => void
}

type GraphDocument = {
  systemDocumentEntryId: string | undefined
  bqDocumentId: string | undefined
}

export function useDocumentGraph(
  pageNumber: number,
  isActiveTab: boolean,
  showRightPage: boolean
): GraphLoadState {
  const graphMode = useIsGraphModeEnabled()
  return useGraphForDocument(
    useDocumentTabDataContext(),
    pageNumber,
    graphMode && isActiveTab,
    showRightPage
  )
}

export function usePreviewGraph(
  document: GraphDocument,
  pageNumber: number
): GraphLoadState {
  const isolating = useIsolationController() !== undefined
  return useGraphForDocument(document, pageNumber, isolating, false)
}

function useGraphForDocument(
  { systemDocumentEntryId, bqDocumentId }: GraphDocument,
  pageNumber: number,
  shown: boolean,
  showRightPage: boolean
): GraphLoadState {
  const { t } = useLingui()
  const locked = useIsGraphLocked()
  const { orgID, siteID } = useKnowledgeDocumentDataContext()
  const { selectedPipelineConfigId, isPipelineConfigScopeLoading } =
    usePipelineConfig()
  const enabled = shown && !locked
  const leftPage = useSystemDocumentPageByNum({
    systemDocumentEntryId,
    bqDocumentId,
    pageNum: pageNumber,
    enabled,
  })
  const rightPage = useSystemDocumentPageByNum({
    systemDocumentEntryId,
    bqDocumentId,
    pageNum: pageNumber + 1,
    enabled: enabled && showRightPage,
  })
  const scope = useMemo(
    () =>
      selectedPipelineConfigId && !isPipelineConfigScopeLoading
        ? {
            organization: orgID,
            site: siteID,
            'pipeline-config': selectedPipelineConfigId,
          }
        : undefined,
    [orgID, siteID, selectedPipelineConfigId, isPipelineConfigScopeLoading]
  )
  const leftGraph = usePageGraph(
    leftPage.page?.entry.id,
    systemDocumentEntryId,
    scope,
    enabled
  )
  const rightGraph = usePageGraph(
    rightPage.page?.entry.id,
    systemDocumentEntryId,
    scope,
    enabled && showRightPage
  )
  const pageGraphs = useMemo(
    (): PageGraphSource[] => [
      ...(leftGraph.graph ? [{ pageNumber, graph: leftGraph.graph }] : []),
      ...(rightGraph.graph && showRightPage
        ? [{ pageNumber: pageNumber + 1, graph: rightGraph.graph }]
        : []),
    ],
    [leftGraph.graph, rightGraph.graph, pageNumber, showRightPage]
  )
  const failed = Boolean(
    leftGraph.error ||
    rightGraph.error ||
    leftPage.error ||
    (showRightPage && rightPage.error)
  )
  const isLoading =
    isPipelineConfigScopeLoading ||
    leftPage.isLoading ||
    leftGraph.isLoading ||
    (showRightPage && (rightPage.isLoading || rightGraph.isLoading))
  let message: string | undefined
  if (enabled) {
    if (failed) message = t`Graph data could not be loaded.`
    else if (isLoading) message = t`Loading graph…`
    else if (!scope) message = t`Graph unavailable for the selected pipeline.`
    else if (pageGraphs.some(({ graph }) => graph.isIncomplete))
      message = pageGraphs.some(({ graph }) =>
        graph.getNodes().some((node) => node.overlays.length > 0)
      )
        ? t`Showing partial graph. Extraction is incomplete.`
        : t`Graph extraction is incomplete. No overlays are available yet.`
    else if (
      !pageGraphs.some(({ graph }) =>
        graph.getNodes().some((node) => node.overlays.length > 0)
      )
    )
      message = t`No graph overlays on this page.`
  }
  return {
    pageGraphs,
    message,
    failed,
    retry: (): void => {
      leftPage.retry()
      rightPage.retry()
      leftGraph.retry()
      rightGraph.retry()
    },
  }
}
